import {
  Action,
  ActionReducer,
  INIT,
  MetaReducer,
  createReducer,
  on,
} from '@ngrx/store';
import { toInteger } from 'lodash';

import {
  AuthFeatureActions,
  CreateAccountActions,
  LoginActions,
} from '@lockton-apps/auth/action';
import { AuthApiActions } from '@lockton-apps/shared/action/auth';
import { RoleType } from '@lockton-apps/shared/interfaces';

import * as AuthActions from './auth.actions';

export const AUTH_FEATURE_KEY = 'auth';

export interface SharedDataAccessAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  requiresMfa: boolean;
  username: string | null;
  nameId: string | null;
  givenName: string | null;
  familyName: string | null;
  emails: string[] | null;
  role: RoleType | null;
  isPrimaryContact: boolean;

  forgotPasswordSubmitted: boolean;

  error?: string | null; // last known error (if any)
}

export interface AuthPartialState {
  readonly [AUTH_FEATURE_KEY]: SharedDataAccessAuth;
}

export const initialAuthState: SharedDataAccessAuth = {
  isLoading: false,
  requiresMfa: false,
  isAuthenticated: false,
  username: null,
  nameId: null,
  givenName: null,
  familyName: null,
  emails: null,
  role: null,
  isPrimaryContact: false,

  forgotPasswordSubmitted: false,

  error: null,
};

const reducer = createReducer(
  initialAuthState,

  on(AuthActions.initSuccess, (state, { session }) => {
    return {
      ...state,
      isAuthenticated: true,
      nameId: session.nameid,
      username: session.unique_name,
      givenName: session.given_name,
      familyName: session.family_name,
      emails: [session.email],
      role: toInteger(session.role) as RoleType,
      isPrimaryContact: session.IsLeadContactForAnyClient === 'True',
    };
  }),

  on(LoginActions.login, CreateAccountActions.formSubmitted, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(AuthFeatureActions.requireTwoFactorAuth, (state) => {
    return {
      ...state,
      isLoading: false,
      isAuthenticated: false,
      requiresMfa: true,
    };
  }),
  on(AuthFeatureActions.twoFactorAuth, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(
    AuthApiActions.loginSuccess,
    AuthApiActions.signUpSuccess,
    (state, { session }) => {
      return {
        ...state,
        isLoading: false,
        requiresMfa: false,
        isAuthenticated: true,
        nameId: session.nameid,
        username: session.unique_name,
        givenName: session.given_name,
        familyName: session.family_name,
        emails: [session.email],
        role: toInteger(session.role) as RoleType,
        isPrimaryContact: session.IsLeadContactForAnyClient === 'True',
      };
    }
  ),
  on(
    AuthApiActions.loginFailure,
    AuthApiActions.signUpFailure,
    AuthApiActions.twoFactorAuthFailure,
    AuthApiActions.resetPasswordFailure,
    AuthApiActions.setPasswordFailure,
    AuthApiActions.changePasswordFailure,
    AuthApiActions.forgotPasswordFailure,
    (state, { message }) => ({
      ...state,
      isLoading: false,
      error: message,
    })
  ),

  on(AuthFeatureActions.forgotPassword, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(AuthApiActions.forgotPasswordSuccess, (state) => ({
    ...state,
    isLoading: false,
    forgotPasswordSubmitted: true,
  })),

  on(AuthFeatureActions.setPassword, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(
    AuthApiActions.resetPasswordSuccess,
    AuthApiActions.setPasswordSuccess,
    AuthApiActions.changePasswordSuccess,
    (state) => ({
      ...state,
      isLoading: false,
    })
  ),

  on(AuthActions.authenticateSession, (state, { session }) => ({
    ...state,
    isAuthenticated: true,
    nameId: session.nameid,
    username: session.unique_name,
    givenName: session.given_name,
    familyName: session.family_name,
    emails: [session.email],
    role: toInteger(session.role) as RoleType,
  })),
  on(
    AuthActions.logoutSuccess,
    AuthActions.logoutFailure,
    AuthActions.sessionTerminated,
    (state) => ({
      ...state,
      isAuthenticated: false,
    })
  )
);

export function authReducer(
  state: SharedDataAccessAuth | undefined,
  action: Action
) {
  return reducer(state, action);
}

export const metaReducers: MetaReducer[] = [
  (reducer: ActionReducer<any>): ActionReducer<any> => {
    return (state, action) => {
      if (
        action !== null &&
        (action.type === AuthActions.logoutSuccess.type ||
          action.type === AuthActions.logoutFailure.type)
      ) {
        return reducer(undefined, { type: INIT });
      }
      return reducer(state, action);
    };
  },
];
